import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";
import moment from "moment";

@Component({
  selector: "rs-date-tree-filter-popover",
  templateUrl: "./index.component.html",
  styleUrls: ["./index.component.scss"],
})
export class DateTreeFilterPopoverComponent implements OnChanges {
  @Input() field = "";
  @Input() translation: any;
  @Input() sourceRows: any[] = [];
  @Input() dateFields: string[] = [];
  @Input() selectedByField: any = {};
  @Input() fieldLabelMap: any = {};

  @Output() apply = new EventEmitter<any>();
  @Output() close = new EventEmitter<void>();

  public keyword = "";
  public years: any[] = [];

  private expandStateByField: any = {};
  private workingSelectedByField: any = {};

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.field ||
      changes.sourceRows ||
      changes.selectedByField ||
      changes.dateFields
    ) {
      this.keyword = "";
      this.workingSelectedByField = this.cloneSelectionMap(
        this.selectedByField
      );
      this.rebuildTree();
    }
  }

  public closePopover(): void {
    this.close.emit();
  }

  public onKeywordChange(value: string): void {
    this.keyword = value || "";
  }

  public getVisibleYears(): any[] {
    const searchText = (this.keyword || "").trim().toLowerCase();
    if (!searchText) {
      return this.years;
    }
    return this.years
      .map((yearNode) => {
        const yearMatched =
          String(yearNode.year).toLowerCase().indexOf(searchText) > -1;
        const months = yearNode.months
          .map((monthNode) => {
            const monthMatched =
              String(monthNode.label)
                .toLowerCase()
                .indexOf(searchText) > -1;
            const days = monthNode.days.filter((dayNode) => {
              return (
                String(dayNode.label)
                  .toLowerCase()
                  .indexOf(searchText) > -1
              );
            });
            if (yearMatched || monthMatched) {
              return monthNode;
            }
            return Object.assign({}, monthNode, { days: days });
          })
          .filter((monthNode) => monthNode.days.length > 0);
        if (yearMatched) {
          return yearNode;
        }
        return Object.assign({}, yearNode, { months: months });
      })
      .filter((yearNode) => yearNode.months.length > 0);
  }

  public toggleYearExpanded(yearNode: any): void {
    const state = this.getOrCreateExpandState(this.field);
    state.years[yearNode.key] = !state.years[yearNode.key];
    yearNode.expanded = !!state.years[yearNode.key];
  }

  public toggleMonthExpanded(monthNode: any): void {
    const state = this.getOrCreateExpandState(this.field);
    state.months[monthNode.key] = !state.months[monthNode.key];
    monthNode.expanded = !!state.months[monthNode.key];
  }

  public isAllChecked(): boolean {
    const selectedMap = this.getFieldDaySelectionMap(this.field);
    const enabledDayKeys = this.getEnabledDayKeysInCurrentField();
    if (!enabledDayKeys.length) {
      return false;
    }
    return enabledDayKeys.every((key) => !!selectedMap[key]);
  }

  public isAllIndeterminate(): boolean {
    const selectedMap = this.getFieldDaySelectionMap(this.field);
    const enabledDayKeys = this.getEnabledDayKeysInCurrentField();
    if (!enabledDayKeys.length) {
      return false;
    }
    const selectedCount = enabledDayKeys.filter((key) => !!selectedMap[key])
      .length;
    return selectedCount > 0 && selectedCount < enabledDayKeys.length;
  }

  public toggleAllDays(checked: boolean): void {
    const selectedMap = this.getFieldDaySelectionMap(this.field);
    const enabledDayKeys = this.getEnabledDayKeysInCurrentField();
    enabledDayKeys.forEach((key) => {
      if (checked) {
        selectedMap[key] = true;
      } else {
        delete selectedMap[key];
      }
    });
  }

  public isDayChecked(dayNode: any): boolean {
    const selectedMap = this.getFieldDaySelectionMap(this.field);
    return !!selectedMap[dayNode.key];
  }

  public toggleDay(dayNode: any, checked: boolean): void {
    if (dayNode.disabled) {
      return;
    }
    const selectedMap = this.getFieldDaySelectionMap(this.field);
    if (checked) {
      selectedMap[dayNode.key] = true;
    } else {
      delete selectedMap[dayNode.key];
    }
  }

  public isMonthDisabled(monthNode: any): boolean {
    return monthNode.days.every((dayNode) => dayNode.disabled);
  }

  public isMonthChecked(monthNode: any): boolean {
    if (this.isMonthDisabled(monthNode)) {
      return false;
    }
    const selectedMap = this.getFieldDaySelectionMap(this.field);
    const enabledDays = monthNode.days.filter((dayNode) => !dayNode.disabled);
    return enabledDays.every((dayNode) => !!selectedMap[dayNode.key]);
  }

  public isMonthIndeterminate(monthNode: any): boolean {
    if (this.isMonthDisabled(monthNode)) {
      return false;
    }
    const selectedMap = this.getFieldDaySelectionMap(this.field);
    const enabledDays = monthNode.days.filter((dayNode) => !dayNode.disabled);
    const selectedCount = enabledDays.filter((dayNode) => {
      return !!selectedMap[dayNode.key];
    }).length;
    return selectedCount > 0 && selectedCount < enabledDays.length;
  }

  public toggleMonth(monthNode: any, checked: boolean): void {
    if (this.isMonthDisabled(monthNode)) {
      return;
    }
    const selectedMap = this.getFieldDaySelectionMap(this.field);
    monthNode.days.forEach((dayNode) => {
      if (dayNode.disabled) {
        return;
      }
      if (checked) {
        selectedMap[dayNode.key] = true;
      } else {
        delete selectedMap[dayNode.key];
      }
    });
  }

  public isYearDisabled(yearNode: any): boolean {
    return yearNode.months.every((monthNode) =>
      this.isMonthDisabled(monthNode)
    );
  }

  public isYearChecked(yearNode: any): boolean {
    if (this.isYearDisabled(yearNode)) {
      return false;
    }
    const enabledMonths = yearNode.months.filter(
      (monthNode) => !this.isMonthDisabled(monthNode)
    );
    return enabledMonths.every((monthNode) => this.isMonthChecked(monthNode));
  }

  public isYearIndeterminate(yearNode: any): boolean {
    if (this.isYearDisabled(yearNode)) {
      return false;
    }
    const selectedMap = this.getFieldDaySelectionMap(this.field);
    const enabledDays = [];
    yearNode.months.forEach((monthNode) => {
      monthNode.days.forEach((dayNode) => {
        if (!dayNode.disabled) {
          enabledDays.push(dayNode.key);
        }
      });
    });
    const selectedCount = enabledDays.filter((key) => !!selectedMap[key]).length;
    return selectedCount > 0 && selectedCount < enabledDays.length;
  }

  public toggleYear(yearNode: any, checked: boolean): void {
    if (this.isYearDisabled(yearNode)) {
      return;
    }
    yearNode.months.forEach((monthNode) => {
      this.toggleMonth(monthNode, checked);
    });
  }

  public clearSelection(): void {
    if (this.field) {
      this.workingSelectedByField[this.field] = {};
    }
    this.apply.emit(this.cloneSelectionMap(this.workingSelectedByField));
    this.rebuildTree();
  }

  public applySelection(): void {
    this.apply.emit(this.cloneSelectionMap(this.workingSelectedByField));
    this.closePopover();
  }

  private rebuildTree(): void {
    if (!this.field) {
      this.years = [];
      return;
    }
    const sourceRows = this.sourceRows || [];
    const rowsByOtherFilters = this.filterRowsBySelectedDateTree(
      sourceRows,
      this.field
    );
    const availableDayKeyMap = {};
    rowsByOtherFilters.forEach((row) => {
      const dayKey = this.getDayKeyByRowValue(row[this.field]);
      if (dayKey) {
        availableDayKeyMap[dayKey] = true;
      }
    });

    const groupedMap = {};
    sourceRows.forEach((row) => {
      const date = this.parseDateValue(row[this.field]);
      if (!date) {
        return;
      }
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      if (!groupedMap[year]) {
        groupedMap[year] = {};
      }
      if (!groupedMap[year][month]) {
        groupedMap[year][month] = {};
      }
      if (!groupedMap[year][month][day]) {
        groupedMap[year][month][day] = 0;
      }
      groupedMap[year][month][day] += 1;
    });

    const expandState = this.getOrCreateExpandState(this.field);
    this.years = Object.keys(groupedMap)
      .map((key) => Number(key))
      .sort((a, b) => b - a)
      .map((year) => {
        const months = Object.keys(groupedMap[year])
          .map((key) => Number(key))
          .sort((a, b) => a - b)
          .map((month) => {
            const monthKey = year + "-" + this.pad2(month);
            if (expandState.months[monthKey] === undefined) {
              expandState.months[monthKey] = true;
            }
            const days = Object.keys(groupedMap[year][month])
              .map((key) => Number(key))
              .sort((a, b) => a - b)
              .map((day) => {
                const dayKey = this.toDayKey(year, month, day);
                const dayDate = new Date(year, month - 1, day);
                return {
                  key: dayKey,
                  day: day,
                  label: moment(dayDate).format("D MMM YYYY"),
                  count: groupedMap[year][month][day],
                  disabled: !availableDayKeyMap[dayKey],
                };
              });
            return {
              key: monthKey,
              month: month,
              label: moment()
                .month(month - 1)
                .format("MMM"),
              expanded: !!expandState.months[monthKey],
              days: days,
            };
          });
        const yearKey = String(year);
        if (expandState.years[yearKey] === undefined) {
          expandState.years[yearKey] = true;
        }
        return {
          key: yearKey,
          year: year,
          expanded: !!expandState.years[yearKey],
          months: months,
        };
      });
  }

  private filterRowsBySelectedDateTree(rows: any[], excludeField?: string) {
    let filteredRows = rows ? rows.slice() : [];
    (this.dateFields || []).forEach((field) => {
      if (excludeField && field === excludeField) {
        return;
      }
      const selectedMap = this.getFieldDaySelectionMap(field);
      const selectedKeys = Object.keys(selectedMap);
      if (!selectedKeys.length) {
        return;
      }
      filteredRows = filteredRows.filter((row) => {
        const dayKey = this.getDayKeyByRowValue(row[field]);
        return dayKey ? !!selectedMap[dayKey] : false;
      });
    });
    return filteredRows;
  }

  private parseDateValue(value: any): Date {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    const parsed = moment(
      value,
      [
        "YYYY-MM-DD",
        "MM/DD/YYYY",
        "DD/MM/YYYY",
        "YYYY/MM/DD",
        "DD-MMM-YYYY",
        "MMM DD, YYYY",
        moment.ISO_8601,
      ],
      true
    );
    if (parsed.isValid()) {
      return parsed.toDate();
    }
    const fallback = new Date(value);
    return isNaN(fallback.getTime()) ? null : fallback;
  }

  private getOrCreateExpandState(field: string) {
    if (!this.expandStateByField[field]) {
      this.expandStateByField[field] = { years: {}, months: {} };
    }
    return this.expandStateByField[field];
  }

  private getFieldDaySelectionMap(field: string) {
    if (!this.workingSelectedByField[field]) {
      this.workingSelectedByField[field] = {};
    }
    return this.workingSelectedByField[field];
  }

  private toDayKey(year: number, month: number, day: number) {
    return year + "-" + this.pad2(month) + "-" + this.pad2(day);
  }

  private pad2(value: number) {
    return value < 10 ? "0" + value : String(value);
  }

  private getDayKeyByRowValue(value: any) {
    const date = this.parseDateValue(value);
    if (!date) {
      return "";
    }
    return this.toDayKey(
      date.getFullYear(),
      date.getMonth() + 1,
      date.getDate()
    );
  }

  private getEnabledDayKeysInCurrentField() {
    const keys = [];
    this.years.forEach((yearNode) => {
      yearNode.months.forEach((monthNode) => {
        monthNode.days.forEach((dayNode) => {
          if (!dayNode.disabled) {
            keys.push(dayNode.key);
          }
        });
      });
    });
    return keys;
  }

  private cloneSelectionMap(source: any) {
    try {
      return source ? JSON.parse(JSON.stringify(source)) : {};
    } catch (e) {
      return {};
    }
  }
}
